import styled from 'styled-components';
import { IoAlertCircle, IoHelpCircle, IoInformationCircle } from 'react-icons/io5';

const ICONS = {
  warn: IoAlertCircle,
  info: IoInformationCircle,
  question: IoHelpCircle,
};

const COLORS = {
  warn: '#F59E0B',
  info: '#3B82F6',
  question: '#3B82F6',
};

/**
 * 공통 확인 모달 (Yes/No)
 *
 * props:
 *  - type: 'warn' | 'info' | 'question' (default 'question')
 *  - title: 타이틀
 *  - message: 본문 (줄바꿈 지원)
 *  - confirmText: 확인 버튼 텍스트 (default '확인')
 *  - cancelText: 취소 버튼 텍스트 (default '취소')
 *  - onConfirm: 확인 클릭 시 호출
 *  - onCancel: 취소 / 오버레이 클릭 시 호출
 */
const ConfirmModal = ({
  type = 'question',
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  onConfirm,
  onCancel,
}) => {
  const Icon = ICONS[type] || IoHelpCircle;
  const color = COLORS[type] || COLORS.question;

  return (
    <Overlay onClick={onCancel}>
      <Box onClick={(e) => e.stopPropagation()}>
        <IconCircle $color={color}>
          <Icon size={36} />
        </IconCircle>
        {title && <Title>{title}</Title>}
        {message && <Message>{message}</Message>}
        <BtnRow>
          <CancelBtn type="button" onClick={onCancel}>
            {cancelText}
          </CancelBtn>
          <ConfirmBtn type="button" onClick={onConfirm}>
            {confirmText}
          </ConfirmBtn>
        </BtnRow>
      </Box>
    </Overlay>
  );
};

export default ConfirmModal;

// ───── 스타일 ─────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Box = styled.div`
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}15;
  border-radius: 50%;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  white-space: pre-line;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  background: #F1F5F9;
  color: ${({ theme }) => theme.colors.text};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;

  &:hover { background: #E2E8F0; }
`;

const ConfirmBtn = styled.button`
  flex: 1;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;

  &:hover { opacity: 0.9; }
`;
